import moment from 'moment-timezone';

/**
 * react-calendar only speaks browser-local `Date`: its tiles are built as
 * `new Date(year, monthIndex, day)` and its `onChange` hands that same value back.
 * The app, meanwhile, runs every `moment()` in the zone baked into the build —
 * `moment.tz.setDefault(config.get('tzName'))` in `containers/Root.jsx`.
 *
 * Those two zones are independent. Reading a tile's instant through moment
 * (`moment(jsDate)`) therefore reinterprets browser-midnight in the app zone, and when
 * the app zone is behind the browser's it lands on the previous calendar day — clicking
 * the 19th stored the 18th on a +07 build viewed from a +08 machine (CPI-1248 follow-up).
 *
 * These two helpers are mirror images that convert by calendar *fields* rather than by
 * instant, so a tile always means the same calendar day on both sides of the boundary.
 */

/**
 * Turn the browser-local `Date` from react-calendar's `onChange` into a moment on that
 * same calendar day in the app's zone, carrying over the time of day from `timeSource`
 * (the value being edited) so picking a date never silently resets the clock. The optional
 * browser-timezone mode is for inputs that deliberately show a viewer's local time.
 */
export function fromCalendarTile(jsDate, timeSource, useBrowserTimezone = false) {
  const date = jsDate instanceof Date && !Number.isNaN(jsDate.getTime()) ? jsDate : new Date();
  const sourceTime = timeSource ? moment(timeSource) : moment();
  const time = useBrowserTimezone ? sourceTime.local() : sourceTime;
  const calendarTime = useBrowserTimezone ? moment().local() : moment();
  return calendarTime.set({
    year: date.getFullYear(),
    month: date.getMonth(),
    date: date.getDate(),
    hour: time.hour(),
    minute: time.minute(),
    second: 0,
    millisecond: 0,
  });
}

/**
 * The inverse: express an app-zone value as the browser-local midnight react-calendar
 * expects for its `value`, `minDate` and `maxDate` props. Without this the highlighted
 * tile drifts whenever the app zone is *ahead* of the browser's. Browser-timezone inputs
 * use the viewer's local calendar fields instead of the app's configured fields.
 */
export function toCalendarTile(value, useBrowserTimezone = false) {
  const m = useBrowserTimezone ? moment(value).local() : moment(value);
  if (!m.isValid()) {
    return undefined;
  }
  return new Date(m.year(), m.month(), m.date());
}



// WEBPACK FOOTER //
// ./src/utils/calendarDate.js